import React, { useState, useEffect } from 'react';
import { aiKeyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sparkles, Loader2, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export const AIKeySettings = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');

  const load = () => {
    aiKeyAPI.getStatus()
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) { toast.error('Paste your OpenAI API key'); return; }
    setSaving(true);
    try {
      const res = await aiKeyAPI.save(apiKey.trim());
      setStatus(res.data);
      setApiKey('');
      toast.success(res.data.warning ? `Key saved — ${res.data.warning}` : 'OpenAI key validated & saved. AI features are now enabled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save key');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the OpenAI key? This disables all AI features until a new key is added.')) return;
    try {
      await aiKeyAPI.remove();
      setStatus({ configured: false });
      toast.success('OpenAI key removed');
    } catch {
      toast.error('Failed to remove key');
    }
  };

  return (
    <Card className="surface" data-testid="ai-key-settings">
      <CardHeader className="border-b border-border">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          AI Provider (OpenAI)
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full brand-gradient text-white flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {status?.configured ? (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25" data-testid="ai-key-connected">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                <div className="flex-1">
                  <p className="font-medium text-sm">AI enabled · key {status.key_hint || '••••'}</p>
                  <p className="text-xs text-muted-foreground">Model {status.model || 'gpt-4o-mini'} · billed to your OpenAI account</p>
                </div>
                {isOwner && (
                  <Button variant="outline" size="sm" onClick={handleRemove} data-testid="ai-key-remove-btn">Remove</Button>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/10 border border-primary/25" data-testid="ai-key-missing">
                <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  AI is currently running on the app&apos;s <span className="text-foreground font-medium">shared credits</span>.
                  Add your own OpenAI key below to bill AI usage (inbox scanning, PO parsing, estimates, quotes) to
                  <span className="text-foreground font-medium"> your own OpenAI account</span> instead.
                </p>
              </div>
            )}

            {isOwner ? (
              <form onSubmit={handleSave} className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Paste your own OpenAI API key so all AI runs on your account. Create one at{' '}
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-primary hover:underline">platform.openai.com/api-keys</a>.
                  It is validated with OpenAI, stored encrypted, and never shown again.
                </p>
                <div className="space-y-2">
                  <Label>OpenAI API key</Label>
                  <Input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="bg-input border-border font-mono"
                    data-testid="ai-key-input"
                  />
                </div>
                <Button type="submit" disabled={saving} data-testid="ai-key-save-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating…</> : <><KeyRound className="h-4 w-4 mr-2" /> {status?.configured ? 'Update key' : 'Save & enable AI'}</>}
                </Button>
              </form>
            ) : (
              <p className="text-xs text-muted-foreground">Only the account owner can set the OpenAI key.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIKeySettings;
